/**
 * posthog-node requires a distinctId per event, and server-side there is no
 * user. The church is the identity: it makes every server event groupable by
 * church, which is how these failures actually cluster (one misconfigured field
 * definition fails for every guest of one church).
 *
 * Lives here rather than in an app because the worker and the Koa API both
 * publish catalog events to PostHog and must agree on identity — two copies
 * would drift, and the same church would split into two people.
 *
 * Sweep RUN events describe a whole pass over every church, so they have no
 * churchId to be identified by. Falling through to 'unknown' would pile all
 * three sweeps onto one meaningless person and make them un-trendable per
 * sweep, so they identify as the sweep itself.
 *
 * Affiliate administration is the third case, for the same reason: an affiliate
 * has no church by definition (ADR 0012), and approvals and payouts are
 * per-affiliate facts. Without this they would all land on 'unknown' together
 * with every other churchless event — the collapse `referral.code_unresolved`
 * was reshaped to avoid. Ordered after churchId so a commission event, which
 * has both, still groups by the church that generated the money.
 *
 * `userId` is deliberately NOT a rung, and the omission is the load-bearing
 * part. Every rung above exists for a subject that has no church to be
 * identified by; a user always has one. Admitting `userId` would mix a
 * person-grain identity into a project whose persons are otherwise churches,
 * sweeps, affiliates and services, and it would silently re-key existing events
 * that carry a `userId` alongside an OPTIONAL `churchId` (`admin.team_*_failed`)
 * depending on whether that optional field happened to be present — the same
 * church splitting into two people, which is the failure this file exists to
 * prevent. An event emitted with a user in hand has a church in hand too: stamp
 * `churchId` on the payload rather than adding a rung here.
 */
export function distinctIdFor(
  properties: Record<string, unknown> | undefined
): string {
  if (typeof properties?.churchId === 'string') return properties.churchId;

  if (typeof properties?.sweep === 'string') return `sweep:${properties.sweep}`;

  if (typeof properties?.affiliateId === 'string') {
    return `affiliate:${properties.affiliateId}`;
  }

  // The wiring canary describes a process, not a church — same shape as the
  // sweep-run case: without this, every boot of both services would pile onto
  // the one 'unknown' person and the per-service liveness signal would vanish.
  if (typeof properties?.service === 'string') {
    return `service:${properties.service}`;
  }

  return 'unknown';
}
